/* eslint-disable @typescript-eslint/indent */
/*
 * @Author: zenghao
 * @Date: 2022-04-18 14:07:31
 * @LastEditors: mingyang_liu 18268117011@163.com
 * @LastEditTime: 2022-07-21 15:30:17
 * @Description: 请填写简介
 */
import { getWeekDayTitle } from '@/pages/dashboard/utils/utils';
import { COLOR_ARR, DEEP_COLORS, LIGHT_COLORS } from '@/pages/dashboard/_config/constant';
import { upIcon, downIcon } from './_config/constants';
import './index.scss';

/**
 * 将 ECharts / CSS 颜色字符串规范为可比较的 6 位小写 #rrggbb（忽略 alpha）。
 * 解决 COLOR_ARR.indexOf 因大小写、rgb 格式与调色板不一致导致匹配失败，
 * 浮窗圆点回退为默认蓝色而折线仍为真实色的问题。
 */
function normalizeColorToHex(color: string): string | null {
  const s = color?.trim();
  if (!s) return null;
  if (s.startsWith('#')) {
    let h = s.slice(1);
    if (h.length === 3) {
      h = h
        .split('')
        .map(c => c + c)
        .join('');
    }
    if (h.length >= 6) {
      return `#${h.slice(0, 6).toLowerCase()}`;
    }
    return null;
  }
  const rgbMatch = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    const r = Math.min(255, parseInt(rgbMatch[1], 10));
    const g = Math.min(255, parseInt(rgbMatch[2], 10));
    const b = Math.min(255, parseInt(rgbMatch[3], 10));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }
  return null;
}

function findPaletteColorIndex(color: string | undefined, palette: string[]): number {
  if (!color || !palette?.length) return -1;
  const target = normalizeColorToHex(color);
  if (!target) return -1;
  return palette.findIndex(c => normalizeColorToHex(c) === target);
}

/** 仅允许写入 style 的 color 片段，避免异常字符串；与 ECharts 常见输出兼容 */
function sanitizeColorForCss(color: string): string {
  const s = String(color).trim();
  if (/^#[0-9a-fA-F]{3,8}$/.test(s)) return s;
  if (/^rgba?\(/i.test(s)) {
    return s.replace(/[<";>]/g, '');
  }
  return '';
}

// 排序方向类型
type TSortOrder = 'asc' | 'desc';

// 排序配置类型（支持向后兼容的 boolean 和新的 asc/desc）
type TSortConfig = boolean | TSortOrder;

interface IEchartsBubbleProps {
  data?: Array<any>;
  className?: string;
  title?: string;
  metricsName?: string;
  ratioName?: string;
  hasRatio?: boolean;
  hasSum?: boolean;
  sumName?: string;
  sumNum?: string;
  ratioStatus?: 'zero' | 'up' | 'down' | 'no';
  sumRatio?: string;
  hasDot?: boolean;
  isSort?: TSortConfig;
  isShowWeekDay?: boolean;
  weekDayArr?: Array<string>;
  isRemoveNull?: boolean;
  isSplit?: boolean;
  isTwoDataCompare?: boolean;
  isColorDepth?: boolean;
  splitData?: Array<{
    title?: string;
    metricsName?: string;
    hasRatio?: boolean;
    ratioName?: string;
    hasSum?: boolean;
    sumName?: string;
    sumNum?: string;
    ratioStatus?: 'zero' | 'up' | 'down' | 'no';
    isRemoveNull?: boolean;
    isSort?: TSortConfig;
    sumRatio?: string;
    hasDot?: boolean;
    data?: Array<any>;
  }>;
  isRetention?: boolean;
  isShowTotal?: boolean;
  extendNode?: any;
  isHideSubtitle?: boolean;
}
export const getEchartTooltipText = (props: IEchartsBubbleProps) => {
  const {
    className = '',
    metricsName = '',
    ratioName = '',
    hasRatio = false,
    hasSum = false,
    data = [],
    sumName = '',
    sumNum = '',
    sumRatio = '',
    ratioStatus,
    hasDot = true,
    isSort,
    isShowWeekDay = false, // 当数据层不好处理时，使用此配置项单独添加周几，饼图一般都需要用
    weekDayArr = [],
    isRemoveNull = false,
    isSplit = false,
    isShowTotal = false,
    isTwoDataCompare = false,
    splitData = [],
    isColorDepth = false,
    extendNode,
    isHideSubtitle = false,
  } = props;

  let { title = '' } = props;

  const flagMap = {
    zero: ' - ',
    up: upIcon,
    down: downIcon,
  };
  // 添加周的说明，需要配合传入周weekDayArr使用
  if (isShowWeekDay) {
    title = getWeekDayTitle(title, weekDayArr);
  }
  const isNull = data?.filter((item) => {
    return item?.metricsNum !== '-' && item?.metricsNum !== '' && item?.metricsNum !== null && item?.name !== 'Total';
  })?.length === 0 && isRemoveNull;

  const renderItem = ({
    title,
    metricsName,
    hasRatio,
    ratioName,
    hasSum,
    sumName,
    sumNum,
    ratioStatus,
    sumRatio,
    hasDot,
    isSort,
    isRemoveNull,
    data,
    index = null
  }: any) => {
    let sortData = data || [];
    if (isSort) {
      // 确定排序方向：true 或 'desc' 为降序，'asc' 为升序
      const sortOrder = isSort === 'asc' ? 'asc' : 'desc';
      sortData = JSON.parse(JSON.stringify(data)).filter(v => v.name !== 'Others' && v.name !== '其他' && !v?.subTitle);
      sortData.sort((a, b) => {
        // 保留负号，只去掉逗号和百分号，以获取真实数值
        const aValue = Number(String(a.metricsNum).replace(/,|%/g, ''));
        const bValue = Number(String(b.metricsNum).replace(/,|%/g, ''));
        const aStr = String(a.metricsNum || '').trim();
        const bStr = String(b.metricsNum || '').trim();
        const isAInvalid = isNaN(aValue) || aStr === '' || aStr === '-';
        const isBInvalid = isNaN(bValue) || bStr === '' || bStr === '-';
        if (isAInvalid && isBInvalid) return 0;
        // 升序和降序时，无效值都排在最后
        if (isAInvalid) return 1;  // a 无效，a 排在后面
        if (isBInvalid) return -1; // b 无效，b 排在后面（a 排在前面）
        // 降序时负数排在正数后面（升序时保持自然顺序）
        if (sortOrder === 'desc') {
          const isANeg = aValue < 0;
          const isBNeg = bValue < 0;
          if (isANeg && !isBNeg) return 1;
          if (!isANeg && isBNeg) return -1;
        }
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      });
      data?.findIndex(v => (v.name === 'Others' || v.name === '其他' || v?.subTitle)) > -1 && sortData.push(data[data?.findIndex(v => (v.name === 'Others' || v.name === '其他' || v?.subTitle))]);
    }
    hasDot = hasDot === undefined ? true : hasDot;
    // 过滤空值
    if (isRemoveNull) {
      sortData = sortData?.filter((item) => {
        return item?.metricsNum !== '-' && item?.metricsNum !== '' && item?.metricsNum !== null;
      });
    }
    const newColors = index ? LIGHT_COLORS : DEEP_COLORS;
    return `
      <div class='db-EchartsBubble__content ${`index_${index}`}'>
        <div class='db-EchartsBubble__title'>
          ${title ? `<span class='db-EchartsBubble__title-text'>${title}</span>` : ''}
          ${metricsName ? `<span class='db-EchartsBubble__title-text'>${metricsName}</span>` : ''}
          ${hasRatio ? `<span class='db-EchartsBubble__title-text'>${ratioName}</span>` : ''}
        </div>
        ${hasSum
        ? `
          <ul class='db-EchartsBubble__list db-EchartsBubble__list--noDot'>
            <li class='db-EchartsBubble__item'>
              <span class='db-EchartsBubble__item-name'>${sumName}</span>
              <span class='db-EchartsBubble__item-metrics'>${sumNum}</span>
              ${hasRatio ? `
              ${(() => {
            if (!ratioStatus) return `<span class='db-EchartsBubble__item-ratio'>${sumRatio}</span>`;
            if (ratioStatus === 'no') return `<span class='db-EchartsBubble__item-ratio status-${ratioStatus}'>-</span>`;
            return `<span class='db-EchartsBubble__item-ratio status-${ratioStatus}'>
                  <span>${sumRatio}</span>
                  ${ratioStatus ? `<span class='anticon svg-icon' ${ratioStatus === 'zero' ? 'style="vertical-align: unset"' : ''}>${flagMap[ratioStatus] ? flagMap[ratioStatus] : ''}</span>` : ''}
                </span>`;
          })()}
                ` : ''}
            </li>
          </ul>
        `
        : ''
      }
        <ul class='db-EchartsBubble__list ${hasDot ? '' : 'db-EchartsBubble__list--noDot'}'>
          ${(() => {
        let result = '';
        sortData?.forEach((item, id) => {
          const { name, metricsNum, ratioNum, color, ratioStatus, icon, customColor, customColorName, subTitle, legendIconType = '' } = item;
          const flag = flagMap[ratioStatus];

          let osName = name?.toLowerCase()?.replace(/\s/g, '');
          if (customColor && name?.trim()?.toLowerCase()?.startsWith('playstation') || name?.trim()?.toLowerCase()?.startsWith('ps')) {
            osName = 'playstation';
          }
          // 有 ECharts 传入的 series 色时，不再用 color-N + home token 映射，避免与经分 option.color 不一致
          const hasSeriesColor = !customColor && !!color && !!sanitizeColorForCss(String(color));
          let colorIndex = -1;
          if (!customColor && !hasSeriesColor) {
            colorIndex = findPaletteColorIndex(
              color,
              isColorDepth ? newColors : COLOR_ARR,
            );
          }

          if (name !== "Total" || isShowTotal) {
            if (subTitle && !isHideSubtitle) {
              result += `<li class='db-EchartsBubble__item sub-title'>${subTitle}</li>`;
            }
            const seriesColorAttr = hasSeriesColor
              ? ` style="--tooltip-dot-color:${sanitizeColorForCss(String(color))}"`
              : '';
            let itemColorClass = '';
            if (customColor) {
              itemColorClass = `custom-color-${customColorName ? customColorName : osName}`;
            } else if (hasSeriesColor) {
              itemColorClass = 'db-EchartsBubble__item--series-color';
            } else {
              itemColorClass = `color-${colorIndex !== -1 ? colorIndex + 1 : ''}`;
            }
            result += `
                <li class='db-EchartsBubble__item ${itemColorClass}'${seriesColorAttr} key=${id}>
                    <span class='db-EchartsBubble__item-name ${legendIconType}'>
                    ${icon ? `<img src=${icon} alt="flag" class='db-EchartsBubble__item-icon' />` : ''}
                    ${name}
                    </span>
                    <span class='db-EchartsBubble__item-metrics tea-text-overflow${metricsNum === '-' ? ' status-no' : ''}'>${metricsNum}</span>
                    ${(() => {
                if (!hasRatio) return '';
                if (ratioStatus === 'no') return `
                          <span class='db-EchartsBubble__item-ratio status-${ratioStatus}'>
                            <span></span>
                            <span class='anticon svg-icon'>-</span>
                          </span>
                        `;
                const status = (!ratioStatus && ratioNum === '-') ? 'no' : ratioStatus;
                return `
                          <span class='db-EchartsBubble__item-ratio status-${status}'>
                            <span>${ratioNum}</span>
                            ${status !== 'no' && status ? `<span class='anticon svg-icon' ${status === 'zero' ? 'style="vertical-align: unset"' : ''}>${flag ? flag : ''}</span>` : ''}
                          </span>
                        `;
              })()
              }
                  </li>
                `;
          }
        });
        return result;
      })()}
        </ul>
      </div>
    `;
  };

  return !isNull ? `
    <div class='db-EchartsBubble ${className} ${isTwoDataCompare && isSplit ? 'horizontal' : ''}'>
      ${(isSplit && splitData?.length > 0) ?
      splitData?.map((item, index) => renderItem({ ...item, index }))
      :
      renderItem({
        title,
        metricsName,
        hasRatio,
        ratioName,
        hasSum,
        sumName,
        sumNum,
        ratioStatus,
        sumRatio,
        hasDot,
        data,
        isRemoveNull,
        isSort,
        index: props.seriesIndex
      })
    }
      ${extendNode ? extendNode : ''}
    </div>
  ` : null;
};
